import React from "react";

import { useApolloClient } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_COUPON, { UPDATE_COUPON } from "../../provider/Coupons/mutation";
import COUPONS from "../../provider/Coupons/query";

/** utils */
import { getGraphsqErros, maskRealBeautify, toFloat } from "../../utilities/functions";
import { useUser } from "../../context/users";
import { Tag } from "primereact/tag";
import moment from "moment";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;
  // const is_root = user?.getUser()?.is_root;

  const toast = React.useRef<any>(null);
  const dt = React.useRef<any>(null);

  const [records, setRecords] = React.useState<any[]>([]);
  const [franchises, setFranchises] = React.useState<iFranchise[]>([]);
  const [regions, setRegions] = React.useState<iRegion[]>([]);
  const [region, setRegion] = React.useState<any>(null);
  const [record, setRecord] = React.useState<iCoupon>();
  const [franchise, setFranchise] = React.useState<any>(null);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [load, setLoad] = React.useState(true);
  const [reload, setReload] = React.useState<boolean>(false);
  const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
  const [selectedRecords, setSelectedRecords] = React.useState<any>(null);
  const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);
  const [deleteRecordsDialog, setDeleteRecordsDialog] = React.useState<boolean>(false);

  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = React.useState<any>(null);

  // const [rush_hour, setRush_hour] = React.useState<iRushHour[]>([])

  let emptyRecord: iCoupon = {
    name: "",
    type: 'UNIQUE',
    value: 0,
    amount: 0,
    is_actived: true,
  };

  const couponType = [
    {
      name: 'Único',
      type: 'UNIQUE',
    },
    {
      name: 'Desconto',
      type: 'DISCOUNT',
    },
    {
      name: 'Desafio',
      type: 'CHALLENGE',
    },
  ];

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    const filter: any = {};

    if (franchise_id) filter.franchise_id = Number(franchise_id);

    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);

    if (franchise?.code) filter.franchise_id = Number(franchise.code);

    if (region?.code) filter.acting_region_id = Number(region.code);

    apollo
      .query({
        query: COUPONS,
        variables: {
          filter: filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.coupons;
        
        setFranchises(response.data.franchises);
        setRegions(response.data.regions);

        setRecords(data);
        setLoading(false);
        setLoad(false);
        setReload(false);
      })
      .catch(error => {
        console.log('Query COUPONS error: ', JSON.stringify(error));
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
        setLoad(false);
        setReload(false);
      });
  };

  const openNew = () => {
    setRecord(emptyRecord);
    setSubmitted(false);
    setRecordDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const hideDeleteRecordsDialog = () => {
    setDeleteRecordsDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (
      !record?.name ||
      !record?.type ||
      !record?.value || record.value === 0 ||
      !record?.amount || record.amount === 0 ||
      !record?.validity
    ) {
      return;
    }

    const _record: any = { ...record };

    if (_record.type !== 'DISCOUNT') {
      _record.value = toFloat(_record.value) * 100;
    }
    
    _record.value = parseInt(`${_record.value}`);

    _record.amount = parseInt(`${_record.amount}`);

    _record.franchise_id = franchise?.code ? parseInt(franchise.code) : undefined;

    if (franchise_id) {
      _record.franchise_id = Number(franchise_id);
    }

    if (acting_region_id) _record.acting_region_id = Number(acting_region_id);

    if (region) {
      _record.acting_region_id = Number(region.code);
    }

    delete _record.__typename;
    delete _record.franchise;
    delete _record.region;
    delete _record.actingRegion;

    setLoading(true);
    let mutation = CREATE_COUPON;

    if (record?.id) {
      _record.id = parseInt(`${_record.id}`);
      mutation = UPDATE_COUPON;
    }

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
        setFranchise(null);
        setRegion(null);
      })
      .catch(error => {
        console.log('UPDATE Coupon error:', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const editRecord = (record: iCoupon) => {
    const _record: iCoupon = { ...record };

    setRecord({ 
      ..._record,
      value: parseFloat(`${_record.value}`) / 100,
    });
    setRecordDialog(true);
  };

  const confirmDeleteRecord = (record: any) => {
    setRecord(record);
    setDeleteRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_COUPON,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        console.log('UPDATE_COUPON delete error: ', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const deleteSelectedRecords = () => {
    let _records = records.filter((val: any) => !selectedRecords.includes(val));
    setRecords(_records);
    setDeleteRecordsDialog(false);
    setSelectedRecords(null);
    toast.current.show({ severity: "success", summary: "Successful", detail: "Records Deleted", life: 3000 });
  };

  const onRoleChange = (e: any) => {
    let _record: any = { ...record };
    _record.is_actived = e.value;
    setRecord(_record);
  };

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    let _record: any = { ...record };

    if (record?.type === 'UNIQUE') {
      _record.name = record.name.toUpperCase();
    }

    if (_record?.type !== 'DISCOUNT' && name === 'value') {
      val = maskRealBeautify(val);
    }

    if (name === 'name' && record?.type === 'UNIQUE') {
      val = val.toUpperCase().replace(/ /g,'');
    }

    if (name === 'type') {
      val = e.value.type;
      _record.value = 0;

      if (val === 'UNIQUE') {
        _record.name = record?.name.toUpperCase();
      }
    }
    _record[name] = val;

    setRecord(_record);
  };

  const formatDate = (date: any) => {
    return `${date.getDate()}/${date.getMonth()+1}/${date.getFullYear()}`;
  };

  const leftToolbarTemplate = () => {
    return (
      <React.Fragment>
        <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
        {/* <Button
          label="Delete"
          icon="pi pi-trash"
          className="p-button-danger mb-2"
          onClick={confirmDeleteSelected}
          disabled={!selectedRecords || !selectedRecords.length}
        /> */}
      </React.Fragment>
    );
  };

  const codeBodyTemplate = (rowData: iCoupon) => {
    return (
      <>
        <span className="p-column-title">Cód.</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iCoupon) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const typeBodyTemplate = (rowData: iCoupon) => {
    switch (rowData.type) {
      case 'CHALLENGE':
        return (
          <>
            <span className="p-column-title">Tipo</span>
            <Tag className="mr-2" severity="info" value={'Desafio'}/>
          </>
        );
      
      case 'DISCOUNT':
        return (
          <>
            <span className="p-column-title">Tipo</span>
            <Tag className="mr-2" severity="success" value={'Desconto'}/>
          </>
        );
    
      default:
        return (
          <>
            <span className="p-column-title">Tipo</span>
            <Tag className="mr-2" value={'Único'}/>
          </>
        );
    }
  };

  const valueBodyTemplate = (rowData: iCoupon) => {
    const type = rowData.type;
    const value = rowData.value;

    return (
      <>
        <span className="p-column-title">Valor</span>
        {type === 'DISCOUNT' ?
          `${value} %` :
          `R$ ${maskRealBeautify(value)}`}
      </>
    );
  };

  const quantityBodyTemplate = (rowData: iCoupon) => {
    return (
      <>
        <span className="p-column-title">Quantidade</span>
        {rowData.amount}
      </>
    );
  };

  const dateBodyTemplate = (rowData: iCoupon) => {
    const date = new Date(rowData?.validity || 0);
    return (
      <>
        <span className="p-column-title">Validade</span>
        {formatDate(date)}
      </>
    );
  };

  const franchiseBodyTemplate = (rowData: iCoupon) => {
    return (
      <>
        <span className="p-column-title">Franquia</span>
        {rowData.franchise?.name}
      </>
    );
  };

  const actingRegionBodyTemplate = (rowData: iCoupon) => {
    return (
      <>
        <span className="p-column-title">Região</span>
        {rowData.actingRegion?.name}
      </>
    );
  };

  const statusBodyTemplate = (rowData: iCoupon) => {
    const validity = moment(rowData.validity);
    const today = moment();

    if (validity && today > validity)
      return (
        <>
          <span className="p-column-title">Situação</span>
          <span className={`product-badge status-disabled`}>Vencido</span>
        </>
      );

    if (rowData.amount <= 0)
      return (
        <>
          <span className="p-column-title">Situação</span>
          <span className={`product-badge status-disabled`}>Esgotado</span>
        </>
      );

    return (
      <>
        <span className="p-column-title">Situação</span>
        <span className={`product-badge status-${!rowData.is_actived ? "disabled" : "actived"}`}>{rowData.is_actived ? "Ativo" : "Inativo"}</span>
      </>
    );
  };

  const actionBodyTemplate = (rowData: iCoupon) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando cupons..." : "Cupons"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  const deleteRecordsDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteRecordsDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedRecords} />
    </>
  );

  React.useEffect(() => {
    loadRecords(true);
  }, [region?.code]);

  React.useEffect(() => {
    if (franchise?.code){
      setRegion(null);
      setReload(true);
      loadRecords(true);
    }
  }, [franchise?.code]);

  return (
    <div className="grid crud-demo">
      {franchise_id && acting_region_id ? null : (
        load ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        ) : (
          <>
            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap">
                { !franchise_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Franquia</label>
                    <div className="field col-12 md:col-12">
                      <Dropdown
                        value={franchise}
                        onChange={e => setFranchise(e.value)}
                        options={franchises.map(i => ({ code: i.id, name: i.name }))}
                        optionLabel="name"
                        placeholder="Selecione uma Franquia"
                      />
                    </div>
                  </div>
                ) : null }
  
  
                { !acting_region_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Região</label>
                    <div className="field col-12 md:col-12">
                      {reload ? (
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                      ) : (
                        <Dropdown
                          value={region}
                          onChange={e => setRegion(e.value)}
                          options={regions.map(i => ({ code: i.id, name: i.name }))}
                          optionLabel="name"
                          placeholder="Selecione uma Região"
                        />
                      )}
                    </div>
                  </div>
                ) : null }
              </div>
            </div>
          </>
        )
      )}
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />
          <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

          <DataTable
            ref={dt}
            value={records}
            dataKey="id"
            paginator
            stripedRows={true}
            rowHover
            showGridlines
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="datatable-responsive"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado"
            header={header}
            responsiveLayout="scroll"
          >
            <Column field="id" header="Cód." sortable body={codeBodyTemplate} headerStyle={{}} />
            <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}} />
            <Column field="type" header="Tipo" sortable body={typeBodyTemplate} headerStyle={{}} />
            <Column field="value" header="Valor" sortable body={valueBodyTemplate} headerStyle={{}} />
            <Column field="quantity" header="Quantidade" sortable body={quantityBodyTemplate} headerStyle={{}} />
            <Column field="validity" header="Validade" sortable body={dateBodyTemplate} headerStyle={{}} />
            { !franchise_id ? (
              <Column field="franchise.name" header="Franquia" sortable body={franchiseBodyTemplate} headerStyle={{}} />
            ) : null }
            { !acting_region_id ? (
              <Column field="actingRegion.name" header="Região" sortable body={actingRegionBodyTemplate} headerStyle={{}} />
            ) : null }

            <Column field="is_actived" header="Situação" body={statusBodyTemplate} sortable headerStyle={{}} />
            <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate} />
          </DataTable>

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes do Cupom" : "Novo Cupom"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}
          >
            <div className="flex flex-column md:flex-row flex-wrap">
              <div className="field col-12 md:col-12">
                <label htmlFor="type">* Tipo</label>
                <Dropdown
                  value={couponType.find(i => (i.type === record?.type))}
                  onChange={e => onInputChange(e, 'type')}
                  options={couponType.map(i => ({ type: i.type, name: i.name }))}
                  optionLabel="name"
                  placeholder="Selecione o tipo de cupom"
                />
                {submitted && !record?.type && <small className="p-invalid">Tipo do cuppom é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-12">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>

              { !franchise_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Franquia</label>
                    <Dropdown
                      value={franchise}
                      onChange={e => setFranchise(e.value)}
                      options={franchises.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Franquia"
                    />
                  </div>
                </div>
              ) : null }

              { !acting_region_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Região</label>
                    <Dropdown
                      value={region}
                      onChange={e => setRegion(e.value)}
                      options={regions.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Região"
                    />
                  </div>
                </div>
              ) : null }

              <div className="field col-12 md:col-4">
                <label htmlFor="value">* Valor {record?.type === 'DISCOUNT' ? '%' : 'R$'}</label>
                <InputText
                  id="value"
                  type={record?.type === 'DISCOUNT' ? 'number' : 'text'}
                  value={record?.value}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "value")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.value })}
                />
                {submitted && (!record?.value || record?.value === 0) && <small className="p-invalid">Valor é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="amount">* Quantidade</label>
                <InputText
                  id="amount"
                  type={"number"}
                  value={record?.amount}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "amount")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.amount })}
                />
                {submitted && (!record?.amount || record?.amount === 0) && <small className="p-invalid">Quantidade é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-12">
                <label htmlFor="validity">* Validade</label>
                <Calendar
                  showIcon
                  showButtonBar
                  value={record?.validity}
                  onChange={e => onInputChange(e, 'validity')}
                  dateFormat="dd/mm/yy"
                  className={classNames("p-3", { "p-invalid": submitted && !record?.validity })}
                />
                {submitted && (!record?.validity) && <small className="p-invalid">Validade é obrigatório.</small>}
              </div>
            </div>

            <hr />
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Situação</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="is_actived" value={true} onChange={onRoleChange} checked={record?.is_actived} />
                    <label htmlFor="role1">Ativo</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="is_actived" value={false} onChange={onRoleChange} checked={!record?.is_actived} />
                    <label htmlFor="role2">Inativo</label>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>

          {/* Modal remover multiplos registros */}
          <Dialog
            visible={deleteRecordsDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordsDialogFooter}
            onHide={hideDeleteRecordsDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && <span>Tem certeza de que deseja excluir os registros selecionados?</span>}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
